/* Use Shopify's filtered result total, never a scan of the first product page. */
(() => {
  if (customElements.get('dut-available-count')) return;
  const cache = new Map();
  const queue = [];
  const ttl = 60000;
  let active = 0;
  function drain() {
    while (active < 3 && queue.length) {
      active++;
      const job = queue.shift();
      job().finally(() => { active--; drain(); });
    }
  }
  function countFor(path) {
    const url = new URL(path, location.origin);
    if (url.origin !== location.origin) return Promise.reject(new Error('Invalid collection URL'));
    if (/\/collections\/all\/?$/.test(url.pathname)) {
      url.pathname = `${window.Shopify?.routes?.root || '/'}collections/in-stock`;
    }
    url.searchParams.set('filter.v.availability', '1');
    url.searchParams.set('section_id', 'dut-available-count');
    const key = `${window.Shopify?.country || ''}:${window.Shopify?.currency?.active || ''}:${url.pathname}${url.search}`;
    const cached = cache.get(key);
    if (cached && Date.now() - cached.time < ttl) return cached.promise;
    const promise = new Promise((resolve, reject) => {
      queue.push(async () => {
        try {
          const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
          if (!response.ok) throw new Error('Count unavailable');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const result = html.querySelector('[data-dut-available-count]');
          const raw = result?.dataset.dutAvailableCount;
          if (!/^\d+$/.test(raw || '')) throw new Error('No filtered count');
          resolve({ value: Number(raw), capped: result.dataset.countCapped === 'true' });
        } catch (error) { cache.delete(key); reject(error); }
      });
    });
    cache.set(key, { time: Date.now(), promise });
    drain();
    return promise;
  }
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => { if (entry.isIntersecting) entry.target.update(); });
  }, { rootMargin: '120px' });
  customElements.define('dut-available-count', class extends HTMLElement {
    connectedCallback() { observer.observe(this); }
    disconnectedCallback() { observer.unobserve(this); }
    async update() {
      if (this.pending || (this.loadedAt && Date.now() - this.loadedAt < ttl)) return;
      if (this.closest('[hidden]') || !this.getClientRects().length) return;
      const closed = this.closest('details:not([open])');
      if (closed && !closed.querySelector(':scope > summary')?.contains(this)) return;
      this.pending = true;
      try {
        const result = await countFor(this.dataset.url);
        const count = result.value;
        const label = result.capped ? `${count.toLocaleString()}+` : String(count);
        if (!this.isConnected) return;
        if (this.dataset.format === 'menu') {
          this.textContent = `(${label})`;
        } else {
          const number = document.createElement('span');
          number.className = 'dut-available-count__number';
          number.textContent = label;
          this.replaceChildren(number, document.createTextNode(' available'));
        }
        this.setAttribute('aria-label', `${result.capped ? 'At least ' : ''}${count} available ${count === 1 ? 'product' : 'products'}`);
        this.dataset.count = String(count);
        this.loadedAt = Date.now();
      } catch (_) {
        // Never fall back to a misleading all-stock total if a request fails.
        this.textContent = '';
        this.removeAttribute('aria-label');
      } finally { this.pending = false; }
    }
  });
  document.addEventListener('toggle', event => {
    if (event.target.open) event.target.querySelectorAll('dut-available-count').forEach(node => node.update());
  }, true);
})();
